/**
 * ScheduledReportsManager — create, manage, and manually trigger scheduled reports.
 */
import { useState, useEffect } from 'react'
import { Plus, Send, Trash2, ToggleLeft, ToggleRight, FileText, Mail } from 'lucide-react'
import {
  scheduledReportsService,
  type ScheduledReport,
  type CreateScheduledReport,
} from '../services/scheduledReports'
import { toast } from 'sonner'
import { DateDisplay } from './ui/DateDisplay'

const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'on_trigger', label: 'On trigger' },
]

const FORMATS = [
  { value: 'pdf', label: 'PDF' },
  { value: 'excel', label: 'Excel' },
  { value: 'json', label: 'JSON' },
]

const REPORT_TYPES = [
  { value: 'status', label: 'Status report' },
  { value: 'budget', label: 'Budget & finance' },
  { value: 'time_tracking', label: 'Time tracking' },
  { value: 'governance', label: 'Governance' },
]

export function ScheduledReportsManager() {
  const [reports, setReports] = useState<ScheduledReport[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState<Omit<CreateScheduledReport, 'report_config'> & {
    entity_type: string
    entity_id: string
    report_type: string
    recipient_emails_raw: string
  }>({
    name: '',
    frequency: 'weekly',
    format: 'pdf',
    recipient_emails: [],
    recipient_emails_raw: '',
    next_send_at: '',
    entity_type: 'project',
    entity_id: '',
    report_type: 'status',
  })

  useEffect(() => { loadReports() }, [])

  const loadReports = async () => {
    setLoading(true)
    try {
      const data = await scheduledReportsService.list()
      setReports(data)
    } catch {
      toast.error('Failed to load scheduled reports')
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    const emails = form.recipient_emails_raw.split(/[\s,]+/).filter(Boolean)
    if (!emails.length) { toast.error('At least one recipient email is required'); return }
    if (!form.entity_id) { toast.error('Entity ID is required'); return }
    if (!form.next_send_at) { toast.error('Next send time is required'); return }

    setCreating(true)
    try {
      const created = await scheduledReportsService.create({
        name: form.name || `${form.report_type} report`,
        frequency: form.frequency as ScheduledReport['frequency'],
        format: form.format as ScheduledReport['format'],
        recipient_emails: emails,
        next_send_at: new Date(form.next_send_at).toISOString(),
        report_config: {
          entity_type: form.entity_type,
          entity_id: parseInt(form.entity_id),
          report_type: form.report_type,
          include_sections: [],
        },
      })
      setReports(prev => [created, ...prev])
      setShowCreate(false)
      toast.success('Scheduled report created')
    } catch {
      toast.error('Failed to create scheduled report')
    } finally {
      setCreating(false)
    }
  }

  const handleTrigger = async (id: number, name: string) => {
    try {
      const result = await scheduledReportsService.trigger(id)
      toast.success(result.message)
    } catch {
      toast.error(`Failed to trigger "${name}"`)
    }
  }

  const handleToggleActive = async (report: ScheduledReport) => {
    try {
      const updated = await scheduledReportsService.update(report.id, { is_active: !report.is_active })
      setReports(prev => prev.map(r => r.id === updated.id ? updated : r))
    } catch {
      toast.error('Failed to update report')
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this scheduled report? Recipients will no longer receive it.')) return
    try {
      await scheduledReportsService.delete(id)
      setReports(prev => prev.filter(r => r.id !== id))
      toast.success('Deleted')
    } catch {
      toast.error('Failed to delete report')
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-neutral-950">Scheduled Reports</h3>
        <button
          onClick={() => setShowCreate(v => !v)}
          className="flex items-center gap-2 px-3 py-2 bg-[#486581] text-white text-sm rounded-lg hover:bg-[#3d5570] transition-colors min-h-[40px]"
          aria-expanded={showCreate}
        >
          <Plus className="w-4 h-4" aria-hidden="true" />
          New schedule
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <form
          onSubmit={handleCreate}
          className="bg-gray-50 border border-gray-200 rounded-xl p-5 space-y-4"
          aria-label="Create scheduled report"
        >
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label htmlFor="sr-name" className="block text-xs font-medium text-gray-700 mb-1">Report name</label>
              <input
                id="sr-name"
                type="text"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Weekly project status"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#486581] min-h-[40px]"
              />
            </div>

            <div>
              <label htmlFor="sr-entity-type" className="block text-xs font-medium text-gray-700 mb-1">Entity type</label>
              <select
                id="sr-entity-type"
                value={form.entity_type}
                onChange={e => setForm(f => ({ ...f, entity_type: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#486581] min-h-[40px]"
              >
                <option value="project">Project</option>
                <option value="program">Program</option>
                <option value="portfolio">Portfolio</option>
              </select>
            </div>

            <div>
              <label htmlFor="sr-entity-id" className="block text-xs font-medium text-gray-700 mb-1">Entity ID</label>
              <input
                id="sr-entity-id"
                type="number"
                value={form.entity_id}
                onChange={e => setForm(f => ({ ...f, entity_id: e.target.value }))}
                placeholder="e.g. 42"
                required
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#486581] min-h-[40px]"
              />
            </div>

            <div>
              <label htmlFor="sr-report-type" className="block text-xs font-medium text-gray-700 mb-1">Report type</label>
              <select
                id="sr-report-type"
                value={form.report_type}
                onChange={e => setForm(f => ({ ...f, report_type: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#486581] min-h-[40px]"
              >
                {REPORT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            <div>
              <label htmlFor="sr-frequency" className="block text-xs font-medium text-gray-700 mb-1">Frequency</label>
              <select
                id="sr-frequency"
                value={form.frequency}
                onChange={e => setForm(f => ({ ...f, frequency: e.target.value as ScheduledReport['frequency'] }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#486581] min-h-[40px]"
              >
                {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>

            <div>
              <label htmlFor="sr-format" className="block text-xs font-medium text-gray-700 mb-1">Format</label>
              <select
                id="sr-format"
                value={form.format}
                onChange={e => setForm(f => ({ ...f, format: e.target.value as ScheduledReport['format'] }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#486581] min-h-[40px]"
              >
                {FORMATS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>

            <div>
              <label htmlFor="sr-next-send" className="block text-xs font-medium text-gray-700 mb-1">First send at</label>
              <input
                id="sr-next-send"
                type="datetime-local"
                value={form.next_send_at}
                onChange={e => setForm(f => ({ ...f, next_send_at: e.target.value }))}
                required
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#486581] min-h-[40px]"
              />
            </div>

            <div className="col-span-2">
              <label htmlFor="sr-recipients" className="block text-xs font-medium text-gray-700 mb-1">
                Recipients <span className="text-gray-400 font-normal">(comma or newline separated)</span>
              </label>
              <textarea
                id="sr-recipients"
                value={form.recipient_emails_raw}
                onChange={e => setForm(f => ({ ...f, recipient_emails_raw: e.target.value }))}
                rows={3}
                placeholder="alice@company.com, bob@client.com"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#486581] resize-none"
              />
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={creating}
              className="px-4 py-2 bg-[#486581] text-white text-sm rounded-lg hover:bg-[#3d5570] disabled:opacity-50 transition-colors min-h-[40px]"
            >
              {creating ? 'Creating…' : 'Create schedule'}
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors min-h-[40px]"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* List */}
      {loading ? (
        <p className="text-sm text-gray-500 text-center py-4">Loading…</p>
      ) : reports.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-gray-200 rounded-xl">
          <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" aria-hidden="true" />
          <p className="text-sm text-gray-500">No scheduled reports</p>
        </div>
      ) : (
        <ul className="space-y-3" role="list" aria-label="Scheduled reports">
          {reports.map(report => (
            <li
              key={report.id}
              className={`border rounded-xl p-4 transition-opacity ${!report.is_active ? 'opacity-60' : ''} border-gray-200`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-sm font-medium text-neutral-950">{report.name}</span>
                    <span className="text-[11px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded border border-gray-200 uppercase font-medium">
                      {report.format}
                    </span>
                    <span className="text-[11px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded border border-blue-200 capitalize">
                      {report.frequency}
                    </span>
                    {!report.is_active && (
                      <span className="text-[11px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded border border-gray-200">Paused</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <Mail className="w-3 h-3" aria-hidden="true" />
                    {report.recipient_emails.length} recipient{report.recipient_emails.length !== 1 ? 's' : ''}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {report.last_sent_at ? <>Last sent <DateDisplay date={report.last_sent_at} /></> : 'Never sent'}
                    {' · '}Next: <DateDisplay date={report.next_send_at} />
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => handleTrigger(report.id, report.name)}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition-colors min-h-[32px]"
                    aria-label="Send now"
                    title="Send now"
                  >
                    <Send className="w-3 h-3" aria-hidden="true" />
                    Send now
                  </button>
                  <button
                    onClick={() => handleToggleActive(report)}
                    className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors min-w-[32px] min-h-[32px] flex items-center justify-center"
                    aria-label={report.is_active ? 'Pause schedule' : 'Resume schedule'}
                    title={report.is_active ? 'Pause' : 'Resume'}
                  >
                    {report.is_active
                      ? <ToggleRight className="w-4 h-4 text-green-600" aria-hidden="true" />
                      : <ToggleLeft className="w-4 h-4 text-gray-400" aria-hidden="true" />}
                  </button>
                  <button
                    onClick={() => handleDelete(report.id)}
                    className="p-1.5 hover:bg-red-50 rounded-lg transition-colors min-w-[32px] min-h-[32px] flex items-center justify-center"
                    aria-label="Delete schedule"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
